import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useEffect } from 'react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { storePendingPolicyChange } from '../../api/equipmentApi';
import { createTicket, fetchContactsByAccount, createContact } from '../../api/ticketData';

// Reusable EditableField component
const EditableField = ({ label, value, onSave, fieldKey, savedChanges }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [hasChanges, setHasChanges] = useState(false);
    
    const hasSavedChanges = savedChanges && savedChanges[fieldKey] !== undefined;

    const handleEdit = () => {
        setIsEditing(true);
        setEditValue(value || '');
    };

    const handleSave = () => {
        if (editValue !== value) {
            onSave(fieldKey, editValue);
            setHasChanges(true);
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsEditing(false);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-xs text-font-secondary font-medium">{label}</label>
                <button
                    onClick={handleEdit}
                    className="text-xs bg-bgSecondary button-container text-accent p-1.5 rounded hover:bg-accent hover:text-background transition-colors duration-300"
                >
                    <Icon icon="mdi:pencil" className="text-sm" />
                </button>
            </div>
            <p className="text-sm text-font-primary">{value || 'N/A'}</p>
            
            {isEditing && (
                <div className="space-y-2">
                    {fieldKey === 'policy_text' ? (
                        <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                            autoFocus
                        />
                    ) : (
                        <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            autoFocus
                        />
                    )}
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            className="text-xs bg-primary text-background px-2 py-1 rounded hover:bg-primary/80 transition-colors duration-300"
                        >
                            Save
                        </button>
                        <button
                            onClick={handleCancel}
                            className="text-xs bg-bgSecondary text-font-primary px-2 py-1 rounded hover:bg-accent hover:text-background transition-colors duration-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            
            {(hasChanges && editValue !== value) || hasSavedChanges ? (
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200 flex flex-row items-start gap-1">
                    <Icon icon="icon-park-outline:switch" className="text-green-600 text-lg" />
                    {hasSavedChanges ? savedChanges[fieldKey] : editValue}
                </div>
            ) : null}
        </div>
    );
};

const EditPolicy = ({ onBack, initialPolicyId, policies }) => {
    const [selectedPolicyId, setSelectedPolicyId] = useState(initialPolicyId || '');
    const [policyChanges, setPolicyChanges] = useState({});
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    
    const selectedPolicy = policies?.find(p => {
        const policyId = p.id || p.policy_id || p.policyId;
        return policyId === selectedPolicyId;
    });

    useEffect(() => {
        if (initialPolicyId) {
            setSelectedPolicyId(initialPolicyId);
        }
    }, [initialPolicyId]);

    const handlePolicySave = (fieldKey, value) => {
        setPolicyChanges(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleReview = () => {
        setIsReviewMode(true);
    };

    const handleBackToEdit = () => {
        setIsReviewMode(false);
    };

    const handleExitAttempt = () => {
        if (Object.keys(policyChanges).length > 0) {
            setShowExitConfirm(true);
        } else {
            onBack();
        }
    };

    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onBack();
    };

    const handleCancelExit = () => {
        setShowExitConfirm(false);
    };

    if (isReviewMode) {
        return (
            <ReviewPolicy
                onBack={handleBackToEdit}
                onSuccess={onBack}
                policyChanges={policyChanges}
                selectedPolicy={selectedPolicy}
            />
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-center text-2xl">
                    <Icon icon="fluent:box-edit-20-regular" className="text-primary text-3xl"></Icon>
                    Edit Policy
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={handleExitAttempt}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Back</span>
                    </button>
                    
                    {Object.keys(policyChanges).length > 0 && (
                        <button 
                            onClick={handleReview}
                            className="flex flex-row gap-2 items-center text-font-primary bg-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary/80 transition-colors duration-300"
                        >
                            <Icon icon="mdi:eye" className="text-xl text-primary group-hover:text-primary" />
                            <span className="text-sm font-medium text-font-primary">Review Changes</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Policy Selector */}
            <div className="flex flex-col items-start gap-2 w-full">
                <div className="flex flex-row items-center gap-1">
                    <Icon icon="material-symbols:policy-rounded" className="text-2xl text-primary" />
                    <label htmlFor="select-policy" className="text-sm text-font-primary font-semibold whitespace-nowrap">Select Policy</label>
                </div>
                <select 
                    id="select-policy" 
                    value={selectedPolicyId}
                    onChange={(e) => setSelectedPolicyId(e.target.value)}
                    className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none"
                >
                    <option value="">Select a policy</option>
                    {policies?.map((policy) => {
                        const policyId = policy.id || policy.policy_id || policy.policyId;
                        const policyName = policy.policy_type || policy.name || 'Unnamed Policy';
                        return (
                            <option key={policyId} value={policyId}>
                                {policyName}
                            </option>
                        );
                    })}
                </select>
            </div>

            {/* Fields Container */}
            {!selectedPolicy ? (
                <div className="flex flex-col items-center justify-center h-full w-full py-16">
                    <Icon icon="material-symbols:policy-rounded" className="text-8xl text-font-secondary mb-4" />
                    <p className="text-2xl text-font-secondary font-medium">Select a Policy</p>
                </div>
            ) : (
                <div className="flex flex-col items-start gap-3 w-full">
                    <div className="flex flex-row items-center gap-1 justify-between w-full">
                        <div className="flex flex-row items-center gap-1">
                            <Icon icon="material-symbols:policy-rounded" className="text-2xl text-accent" />
                            <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Policy Details</label>
                        </div>
                        <div className="flex flex-row items-center gap-1">
                            <label className="text-xs text-font-secondary font-medium">Policy ID:</label>
                            <p className="text-sm text-font-primary font-mono font-medium">{selectedPolicy.id || selectedPolicy.policy_id || selectedPolicy.policyId || 'N/A'}</p>
                        </div>
                    </div>
                    <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                        <EditableField
                            label="Policy Type"
                            value={selectedPolicy.policy_type}
                            onSave={handlePolicySave}
                            fieldKey="policy_type"
                            savedChanges={policyChanges}
                        />
                        <hr className="w-full border-t-2 border-bgAccent" />
                        <EditableField
                            label="Policy Text"
                            value={selectedPolicy.policy_text}
                            onSave={handlePolicySave}
                            fieldKey="policy_text"
                            savedChanges={policyChanges}
                        />
                    </div>
                </div>
            )}

            {/* Exit Confirmation Modal */}
            {showExitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-font-primary mb-4">Unsaved Changes</h3>
                        <p className="text-sm text-font-secondary mb-6">
                            You have unsaved changes. Are you sure you want to exit?
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleCancelExit}
                                className="px-4 py-2 bg-bgSecondary text-font-primary rounded hover:bg-accent transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmExit}
                                className="px-4 py-2 bg-primary text-background rounded hover:bg-primary/80 transition-colors"
                            >
                                Exit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Review Policy Component
const ReviewPolicy = ({ onBack, onSuccess, policyChanges, selectedPolicy }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const user = useAuthUser();

    // Get or create contact in the specified account (Wards)
    const getOrCreateContact = async (email, accountId) => {
        try {
            // First, try to find existing contact in this account
            const contacts = await fetchContactsByAccount(accountId);
            const existingContact = contacts.find(
                (c) => c.email === email && (c.accountId === accountId || c.account?.id === accountId)
            );

            if (existingContact) {
                console.log('ReviewPolicy: Found existing contact:', existingContact.id || existingContact.contactId);
                return existingContact.id || existingContact.contactId;
            }

            // Contact doesn't exist - create it in the specified account
            console.log('ReviewPolicy: Creating new contact in account:', accountId);
            const emailParts = email.split('@')[0].split('.');
            const firstName = emailParts[0] || 'Contact';
            const lastName = emailParts[1] || 'User';

            const newContact = await createContact({
                email: email,
                accountId: accountId,
                firstName: firstName,
                lastName: lastName
            });

            console.log('ReviewPolicy: Created new contact:', newContact.id || newContact.contactId);
            return newContact.id || newContact.contactId;
        } catch (error) {
            console.error('ReviewPolicy: Error getting/creating contact:', error);
            throw new Error(`Failed to get or create contact: ${error.message}`);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const userEmail = user?.email || 'unknown@example.com';
            const customerId = 'wards';
            
            if (Object.keys(policyChanges).length > 0 && selectedPolicy) {
                // Convert policyId to string (backend expects string)
                const policyId = String(selectedPolicy.id || selectedPolicy.policy_id || selectedPolicy.policyId);
                
                // Create original data snapshot - only include fields the backend expects
                const originalData = {
                    policy_type: selectedPolicy.policy_type || '',
                    policy_text: selectedPolicy.policy_text || ''
                };

                // Create modified data - only include fields the backend expects
                const modifiedData = {
                    policy_type: policyChanges.policy_type !== undefined ? policyChanges.policy_type : originalData.policy_type,
                    policy_text: policyChanges.policy_text !== undefined ? policyChanges.policy_text : originalData.policy_text
                };

                const changeData = {
                    policyId: policyId,
                    originalData: originalData,
                    modifiedData: modifiedData,
                    changeType: 'policy',
                    changeAction: 'update'
                };

                console.log('Submitting policy change:', changeData);
                await storePendingPolicyChange(customerId, changeData);
                console.log('✅ Policy change stored in Cosmos DB successfully');

                // Step 2: Create Zoho Desk ticket (always create a ticket for tracking)
                console.log('🔄 Creating Zoho Desk ticket...');
                
                try {
                    // Wards account ID
                    const wardsAccountId = '1094595000000560001';
                    
                    // Get or create contact in Wards account for mohit.bansal@helios-core.com
                    const assigneeEmail = 'mohit.bansal@helios-core.com';
                    console.log('ReviewPolicy: Getting/creating contact for:', assigneeEmail, 'in account:', wardsAccountId);
                    
                    const contactId = await getOrCreateContact(assigneeEmail, wardsAccountId);
                    console.log('ReviewPolicy: Contact ID obtained:', contactId);

                    // Build subject
                    const subject = `Review Policy: ${selectedPolicy.policy_type || 'Unknown Policy'}`;

                    // Build description
                    let description = `Policy: ${selectedPolicy.policy_type || 'Unknown Policy'}<br>`;
                    description += '<br>-------<br><br>';

                    // Add policy changes
                    if (Object.keys(policyChanges).length > 0) {
                        description += 'Policy Changes:<br><br>';
                        Object.keys(policyChanges).forEach(fieldKey => {
                            const fieldLabel = fieldKey === 'policy_type' ? 'Policy Type' : 
                                             fieldKey === 'policy_text' ? 'Policy Text' : 
                                             fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                            const originalValue = originalData[fieldKey] || 'N/A';
                            const newValue = policyChanges[fieldKey];
                            
                            description += `${fieldLabel} - ${originalValue}<br><br>`;
                            description += `Edits: ${newValue}<br><br>`;
                            description += '----<br><br>';
                        });
                    }

                    // Create ticket data
                    const ticketData = {
                        subject: subject,
                        description: description,
                        email: assigneeEmail,
                        contactId: contactId,
                        departmentId: '1094595000000006907',
                        classification: 'Problem',
                        priority: 'High',
                        channel: 'Web',
                        language: 'en',
                        accountId: wardsAccountId,
                    };

                    console.log('ReviewPolicy: Creating ticket with data:', {
                        accountId: ticketData.accountId,
                        contactId: ticketData.contactId,
                        email: ticketData.email,
                        subject: ticketData.subject
                    });

                    await createTicket(ticketData);
                    console.log('✅ Zoho Desk ticket created successfully');
                } catch (ticketError) {
                    console.error('❌ ReviewPolicy: Failed to create ticket:', ticketError);
                    // Don't throw - allow the submission to succeed even if ticket creation fails
                    console.warn('⚠️ ReviewPolicy: Ticket creation failed, but changes were saved to Cosmos DB');
                }

                setSubmitSuccess(true);
                
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to submit changes:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // Parse error response - handle FastAPI/Pydantic validation errors
            let errorMessage = 'Failed to submit changes';
            const errorData = error.response?.data;
            
            if (errorData) {
                // Handle validation error array (FastAPI format)
                if (Array.isArray(errorData.detail)) {
                    errorMessage = errorData.detail.map(err => {
                        if (typeof err === 'object' && err.msg) {
                            const loc = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
                            return loc ? `${loc}: ${err.msg}` : err.msg;
                        }
                        return typeof err === 'string' ? err : JSON.stringify(err);
                    }).join('; ');
                }
                // Handle single validation error object
                else if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.msg) {
                    const loc = Array.isArray(errorData.detail.loc) ? errorData.detail.loc.slice(1).join('.') : '';
                    errorMessage = loc ? `${loc}: ${errorData.detail.msg}` : errorData.detail.msg;
                }
                // Handle string detail
                else if (typeof errorData.detail === 'string') {
                    errorMessage = errorData.detail;
                }
                // Handle message field
                else if (errorData.message) {
                    errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
                }
            }
            
            // Fallback to error message if nothing else worked
            if (errorMessage === 'Failed to submit changes' && error.message) {
                errorMessage = error.message;
            }
            
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="responsive-page-layout scrollable">
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-center text-2xl">
                    <Icon icon="mdi:eye" className="text-primary text-3xl"></Icon>
                    Review Changes
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={onBack}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Back to Edit</span>
                    </button>
                    
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex flex-row gap-2 items-center text-font-primary bg-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary/80 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon 
                            icon="material-symbols:rate-review-rounded" 
                            className="text-xl text-primary group-hover:text-primary" 
                        />
                        <span className="text-sm font-medium text-font-primary group-hover:text-primary">
                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </span>
                    </button>
                </div>
            </div>

            {submitSuccess && (
                <div className="w-full bg-green-900 text-green-300 border border-green-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:check-circle-outline" className="text-xl" />
                        <span className="text-sm">Review submitted successfully!</span>
                    </div>
                </div>
            )}

            {submitError && (
                <div className="w-full bg-red-900 text-red-300 border border-red-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:error-outline" className="text-xl" />
                        <span className="text-sm">{submitError}</span>
                    </div>
                </div>
            )}

            <div className="flex flex-col items-start gap-3 w-full">
                <div className="flex flex-row items-center gap-1 justify-between w-full">
                    <div className="flex flex-row items-center gap-1">
                        <Icon icon="material-symbols:policy-rounded" className="text-2xl text-accent" />
                        <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Policy Details</label>
                    </div>
                </div>
                <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                    {Object.keys(policyChanges).map((fieldKey) => {
                        const fieldLabel = fieldKey === 'policy_type' ? 'Policy Type' : 'Policy Text';
                        return (
                            <div key={fieldKey}>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs text-font-secondary font-medium">{fieldLabel}</label>
                                        <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200 flex items-center gap-1">
                                            <Icon icon="icon-park-outline:switch" className="text-green-600 text-sm" />
                                            Modified
                                        </div>
                                    </div>
                                    <p className="text-sm text-font-primary">{policyChanges[fieldKey] || 'N/A'}</p>
                                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-200">
                                        <strong>Original:</strong> {selectedPolicy[fieldKey] || 'N/A'}
                                    </div>
                                </div>
                                <hr className="w-full border-t-2 border-bgAccent mt-3" />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EditPolicy;

